import React from 'react';
import DOMPurify from 'dompurify';
import { Scenario, Category } from '../../types';
import Button from '../../components/Button';
import { TestManagerNotes } from './TestManagerNotes';

export interface TestManagerContentProps {
  currentScenario: Scenario;
  currentCategory: string;
  categories: Category[];
  localNotes: string;
  isAddingNotes: boolean;
  notesTextareaRef: React.RefObject<HTMLTextAreaElement | null>;
  onStatusChange: (status: string) => void;
  onEditScenario: (scenario: Scenario) => void;
  onDuplicateScenario: (scenario: Scenario) => void;
  onDeleteScenario: (scenario: Scenario) => void;
  onNavigatePrevious: () => void;
  onNavigateNext: () => void;
  onClearScenario: () => void;
  onNotesChangeLocal: (notes: string) => void;
  onNotesSave: () => void;
  onNotesKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onNotesFocus: () => void;
}

export const TestManagerContent: React.FC<TestManagerContentProps> = ({
  currentScenario,
  currentCategory,
  categories,
  localNotes,
  isAddingNotes,
  notesTextareaRef,
  onStatusChange,
  onEditScenario,
  onDuplicateScenario,
  onDeleteScenario,
  onNavigatePrevious,
  onNavigateNext,
  onClearScenario,
  onNotesChangeLocal,
  onNotesSave,
  onNotesKeyDown,
  onNotesFocus,
}) => {
  const currentCategoryScenarios = categories.find((cat) => cat.name === currentCategory)?.scenarios;
  const currentIndex = currentCategoryScenarios?.findIndex((s) => s['ID testu'] === currentScenario['ID testu']);
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === (currentCategoryScenarios?.length ?? 0) - 1;
  const prevScenario = currentIndex != null && currentIndex > 0 && currentCategoryScenarios
    ? currentCategoryScenarios[currentIndex - 1]
    : null;
  const nextScenario = currentIndex != null && currentCategoryScenarios && currentIndex < currentCategoryScenarios.length - 1
    ? currentCategoryScenarios[currentIndex + 1]
    : null;

  return (
    <div className="bg-white dark:bg-slate-700/80 rounded-2xl shadow-xl p-6 transform transition-all duration-200 hover:shadow-2xl">
      <div className="flex justify-between items-center mb-6">
        <div className="flex flex-col">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            {currentScenario['Nazwa testu']}
          </h2>
          {currentCategoryScenarios != null && typeof currentIndex === 'number' && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Test {currentIndex + 1} z {currentCategoryScenarios.length}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onEditScenario(currentScenario)}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-slate-600 rounded-lg transition-all duration-200"
            title="Edytuj test"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => onDuplicateScenario(currentScenario)}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-slate-600 rounded-lg transition-all duration-200"
            title="Duplikuj test"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
          <button
            onClick={() => onDeleteScenario(currentScenario)}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all duration-200"
            title="Usuń test"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
          <button
            onClick={onNavigatePrevious}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-slate-600 rounded-lg transition-all duration-200 relative group"
            title="Poprzedni test"
            disabled={isFirst}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              {prevScenario ? `${prevScenario['ID testu']} - ${prevScenario['Nazwa testu']}` : 'Brak poprzedniego testu'}
            </span>
          </button>
          <button
            onClick={onNavigateNext}
            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 relative group"
            title="Następny test"
            disabled={isLast}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              {nextScenario ? `${nextScenario['ID testu']} - ${nextScenario['Nazwa testu']}` : 'Brak następnego testu'}
            </span>
          </button>
          <button
            onClick={onClearScenario}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all duration-200"
            title="Zamknij test"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          Kroki testowe
        </h3>
        <div className="bg-gray-50 dark:bg-slate-600/60 rounded-xl p-6">
          {currentScenario['Kroki testowe'].split('\n').map((step, index) => (
            <div key={index} className="flex items-start gap-3 mb-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-300 flex items-center justify-center font-medium text-sm">
                {index + 1}
              </div>
              <div
                className="text-gray-700 dark:text-gray-100 prose prose-sm max-w-none dark:prose-invert prose-p:dark:text-gray-100 prose-li:dark:text-gray-100"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(step, {
                    ALLOWED_TAGS: ['strong', 'em', 'u', 'b', 'i', 'br', 'p', 'ul', 'ol', 'li'],
                    ALLOWED_ATTR: [],
                  }),
                }}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Oczekiwany wynik
        </h3>
        <div className="bg-gray-50 dark:bg-slate-600/60 rounded-xl p-6">
          {currentScenario['Oczekiwany rezultat']
            .replace(/\\n/g, '\n')
            .split('\n')
            .map((line, index) => (
              <div
                key={index}
                className="text-gray-700 dark:text-gray-100 prose prose-sm max-w-none dark:prose-invert prose-p:dark:text-gray-100 prose-li:dark:text-gray-100 mb-2"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(line, {
                    ALLOWED_TAGS: ['strong', 'em', 'u', 'b', 'i', 'br', 'p', 'ul', 'ol', 'li'],
                    ALLOWED_ATTR: [],
                  }),
                }}
              />
            ))}
        </div>
      </div>

      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Status
        </h3>
        <div className="flex flex-wrap gap-3">
          {['Passed', 'Failed', 'Blocked', 'Not Tested'].map((status) => (
            <Button
              key={status}
              onClick={() => onStatusChange(status)}
              color={
                status === 'Passed' ? 'green' :
                status === 'Failed' ? 'red' :
                status === 'Blocked' ? 'yellow' :
                'gray'
              }
              variant="status"
              isActive={currentScenario.Status === status}
              icon={
                status === 'Passed' ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : status === 'Failed' ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : status === 'Blocked' ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )
              }
              text={status}
            />
          ))}
        </div>
      </div>

      <TestManagerNotes
        localNotes={localNotes}
        isAddingNotes={isAddingNotes}
        notesTextareaRef={notesTextareaRef}
        onNotesChangeLocal={onNotesChangeLocal}
        onNotesSave={onNotesSave}
        onNotesKeyDown={onNotesKeyDown}
        onFocus={onNotesFocus}
      />
    </div>
  );
};
